import { useState, useCallback } from 'react';

/**
 * Centralizes state and logic for the batch progress modal (one section per action).
 * Progress is keyed by index (position in the list) so multiple actions with the same id each have their own state.
 * @returns {{ isBatchProgressModalOpen: boolean, batchProgressActionItems: BatchProgressItem[], actionProgressList: BatchActionProgressState[], openProgressModal: (items: BatchProgressItem[], selectedRowsCount: number) => void, closeProgressModal: () => void, updateActionProgress: (actionIndex: number, payload: { completed?: number, status?: 'running' | 'done', message?: string, isError?: boolean, openTab?: ApiBatchSetAttributeOpenTabAction }) => void }}
 */
function useBatchActionsProgression() {
  const [isBatchProgressModalOpen, setIsBatchProgressModalOpen] = useState(false);
  const [batchProgressActionItems, setBatchProgressActionItems] = useState([]);
  const [actionProgressList, setActionProgressList] = useState(/** @type {BatchActionProgressState[]} */ ([]));

  const openProgressModal = useCallback(
    (/** @type {BatchProgressItem[]} */ items, /** @type {number} */ selectedRowsCount) => {
      setBatchProgressActionItems(items);
      const initial = /** @type {BatchActionProgressState[]} */ (items.map(() => ({
        status: 'pending',
        completed: 0,
        errorCount: 0,
        total: selectedRowsCount,
        messages: /** @type {string[]} */ ([]),
        openTab: /** @type {ApiBatchSetAttributeOpenTabAction | null} */ (null),
      })));
      setActionProgressList(initial);
      setIsBatchProgressModalOpen(true);
    },
    [],
  );

  const closeProgressModal = useCallback(() => {
    setIsBatchProgressModalOpen(false);
  }, []);

  const updateActionProgress = useCallback((
    /** @type {number} */ actionIndex,
    /** @type {{ completed?: number, status?: 'running' | 'done', message?: string, isError?: boolean, openTab?: ApiBatchSetAttributeOpenTabAction }} */ payload,
  ) => {
    setActionProgressList((prev) => {
      const current = prev[actionIndex];
      if (!current) return prev;
      const next = { ...current };
      if (payload.completed !== undefined) next.completed = payload.completed;
      if (payload.isError) next.errorCount = (current.errorCount ?? 0) + 1;
      if (payload.status !== undefined) next.status = payload.status;
      if (payload.message !== undefined && payload.message) {
        next.messages = [...current.messages, payload.message];
      }
      if (payload.openTab && !current.openTab) {
        next.openTab = payload.openTab;
      }
      return prev.map((s, i) => (i === actionIndex ? next : s));
    });
  }, []);

  return {
    isBatchProgressModalOpen,
    batchProgressActionItems,
    actionProgressList,
    openProgressModal,
    closeProgressModal,
    updateActionProgress,
  };
}

export default useBatchActionsProgression;
